import { useMemo } from 'react';
import {
  INCIDENT_ID,
  MODEL_ID,
  TEST_ID,
  TEST_OVERVIEW_SIDEPANEL_TAB,
  TEST_OVERVIEW_TAB_EXECUTION_INFO,
} from '@elementary/common-ui/constants/urlParams';
import { useTicketingIntegration } from 'shared/hooks/integrations';
import { SourceType } from 'shared/types';
import { GraphQLStatus, Status } from 'incidents/types';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useCreateTicket from './useCreateTicket';

type Props = {
  sourceType: SourceType;
  sourceId: string;
  incidentId: string;
  status: Status | GraphQLStatus;
  ticketsCount?: number;
};

const useIncidentTicketing = ({
  sourceType,
  sourceId,
  incidentId,
  status,
  ticketsCount = 0,
}: Props) => {
  const selectedEnvId = useSelectedEnv();
  const { ticketingQuery } = useTicketingIntegration({ envId: selectedEnvId });

  const enabledIntegrations = useMemo(
    () =>
      ticketingQuery?.data?.integrations.filter(
        (integration) => integration.enabled,
      ),
    [ticketingQuery?.data?.integrations],
  );

  const sourceIdParam = sourceType === 'model' ? MODEL_ID : TEST_ID;
  const incidentUrl = selectedEnvId
    ? `${window.location.origin}/${selectedEnvId}/incidents?${sourceIdParam}=${sourceId}&${INCIDENT_ID}=${incidentId}&${TEST_OVERVIEW_SIDEPANEL_TAB}=${TEST_OVERVIEW_TAB_EXECUTION_INFO}`
    : '';

  const {
    ticketDialogOpen,
    selectedIntegration,
    onCreateTicketClick,
    handleDialogConfirm,
    handleDialogClose,
    isCreatingTicket,
    dialogError,
  } = useCreateTicket({
    integrations: enabledIntegrations,
    incidentUrl,
    incidentId,
  });

  const handleViewTicket = (url: string) => {
    window.open(url, '_blank');
  };

  const isResolved = status === 'Resolved';
  const hasTickets = ticketsCount > 0;
  const hasSingleIntegration = enabledIntegrations?.length === 1;
  const hasMultipleIntegrations = (enabledIntegrations?.length ?? 0) > 1;

  return {
    selectedEnvId,
    enabledIntegrations,
    ticketDialogOpen,
    selectedIntegration,
    onCreateTicketClick,
    handleDialogConfirm,
    handleDialogClose,
    isCreatingTicket,
    dialogError,
    handleViewTicket,
    incidentUrl,
    isResolved,
    hasTickets,
    hasSingleIntegration,
    hasMultipleIntegrations,
  };
};

export default useIncidentTicketing;
